'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/context/LanguageContext';

const navItems = [
  { key: 'nav-overview', href: '/admin-panel', label: 'Overview', icon: 'Squares2X2Icon', badge: null },
  { key: 'nav-bookings', href: '/admin-panel', label: 'Bookings', icon: 'CalendarDaysIcon', badge: '12' },
  { key: 'nav-customers', href: '/admin-panel', label: 'Customers', icon: 'UsersIcon', badge: null },
  { key: 'nav-maids', href: '/admin-panel', label: 'Maids', icon: 'UserGroupIcon', badge: '3' },
  { key: 'nav-apartments', href: '/admin-panel', label: 'Apartments', icon: 'BuildingOffice2Icon', badge: null },
  { key: 'nav-payments', href: '/admin-panel', label: 'Payments', icon: 'CreditCardIcon', badge: '5' },
  { key: 'nav-messages', href: '/admin-panel', label: 'Manager Inbox', icon: 'ChatBubbleLeftRightIcon', badge: '8' },
  { key: 'nav-reports', href: '/admin-panel', label: 'Reports', icon: 'ChartBarIcon', badge: null },
  { key: 'nav-settings', href: '/admin-panel', label: 'Settings', icon: 'Cog6ToothIcon', badge: null },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeNav, setActiveNav] = useState('nav-overview');
  const { t } = useLanguage();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-border flex-shrink-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 h-16 border-b border-border flex-shrink-0 ${collapsed ? 'justify-center' : ''}`}>
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-display font-bold text-base text-foreground truncate">ServeNest</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 overflow-y-auto scrollbar-thin">
          {/* Main group */}
          {!collapsed && (
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">
              Main
            </p>
          )}
          {navItems.slice(0, 6).map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveNav(item.key)}
              className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg mb-0.5 transition-all duration-150 group relative ${
                activeNav === item.key
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon name={item.icon as 'Squares2X2Icon'} size={18} className="flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="text-sm font-medium flex-1 text-left truncate">{item.label}</span>
                  {item.badge && (
                    <span className="text-xs bg-primary text-white px-1.5 py-0.5 rounded-full font-semibold">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
              )}
            </button>
          ))}

          <div className="my-3 border-t border-border" />

          {!collapsed && (
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">
              Communication
            </p>
          )}
          {navItems.slice(6, 8).map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveNav(item.key)}
              className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg mb-0.5 transition-all duration-150 relative ${
                activeNav === item.key
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon name={item.icon as 'ChatBubbleLeftRightIcon'} size={18} className="flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="text-sm font-medium flex-1 text-left truncate">{item.label}</span>
                  {item.badge && (
                    <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-semibold">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
              )}
            </button>
          ))}

          <div className="my-3 border-t border-border" />

          {!collapsed && (
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">
              System
            </p>
          )}
          {navItems.slice(8).map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveNav(item.key)}
              className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg mb-0.5 transition-all duration-150 ${
                activeNav === item.key
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon name={item.icon as 'Cog6ToothIcon'} size={18} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium flex-1 text-left truncate">{item.label}</span>
              )}
            </button>
          ))}
        </nav>

        {/* Bottom: collapse toggle + user */}
        <div className="border-t border-border p-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={18} />
            {!collapsed && <span className="text-sm font-medium">Collapse</span>}
          </button>
          {!collapsed && (
            <div className="flex items-center gap-3 px-2 py-2 mt-1">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">JR</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">Jonathan Reeves</p>
                <p className="text-xs text-muted-foreground truncate">Super Admin</p>
              </div>
              <button className="text-muted-foreground hover:text-foreground transition-colors" title="Sign out">
                <Icon name="ArrowRightOnRectangleIcon" size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-2">
              <AppLogo size={28} />
              <span className="font-display font-bold text-base text-foreground">ServeNest</span>
            </div>
            <div className="hidden lg:block">
              <h1 className="font-display font-semibold text-base text-foreground">Admin Dashboard</h1>
              <p className="text-xs text-muted-foreground">Last updated: Apr 22, 2026 at 2:42 PM</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <Icon name="MagnifyingGlassIcon" size={16} className="text-muted-foreground" />
              <input
                type="text"
                placeholder="Search..."
                className="bg-transparent text-sm outline-none w-40 text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <LanguageSwitcher compact />
            <button className="relative p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <Icon name="BellIcon" size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-destructive rounded-full" />
            </button>
            <Link
              href="/services-booking"
              className="hidden md:flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
            >
              <Icon name="ArrowTopRightOnSquareIcon" size={14} />
              View Site
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
}